import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const QUICK_AMOUNTS = [
    { value: 100000, label: "۱۰۰,۰۰۰" },
    { value: 200000, label: "۲۰۰,۰۰۰" },
    { value: 500000, label: "۵۰۰,۰۰۰" },
    { value: 1000000, label: "۱,۰۰۰,۰۰۰" },
];

const MIN_AMOUNT = 50000;

export default function WalletCharge() {
    const { isAuthenticated, accessToken, loading: loadingauth } = useAuth();
    const navigate = useNavigate();

    const [walletBalance, setWalletBalance] = useState("0");
    const [amount, setAmount] = useState<number | "">("");
    const [error, setError] = useState("");
    const [isPaying, setIsPaying] = useState(false);

    const fetchBalance = async () => {
        try {
            const res = await api.get("/wallet/balance", {
                headers: { Authorization: accessToken ? `Bearer ${accessToken}` : "" },
            });
            if (res.data?.balance) setWalletBalance(res.data.balance);
        } catch (err) {
            console.error("balance fetch error:", err);
        }
    };

    useEffect(() => {
        if (loadingauth) return;

        if (!isAuthenticated) {
            navigate('/');
            return;
        }
        fetchBalance();
    }, [isAuthenticated, loadingauth]);

    const selectQuick = (value: number) => {
        setAmount(value);
        setError("");
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setAmount(val === "" ? "" : Number(val));
        setError("");
    };

    const validate = (): boolean => {
        if (!amount || Number(amount) < MIN_AMOUNT) {
            setError(`حداقل مبلغ شارژ ${MIN_AMOUNT.toLocaleString("fa-IR")} تومان است.`);
            return false;
        }
        return true;
    };

    const handlePay = async () => {
        if (isPaying) return;
        if (!validate()) return;

        setIsPaying(true);
        try {
            const res = await api.post(
                "/wallet/charge",
                { amount: Number(amount) },
                { headers: { Authorization: accessToken ? `Bearer ${accessToken}` : "" } }
            );

            if (res.data?.payment_url) {
                window.location.href = res.data.payment_url;
            } else {
                setError("خطا در دریافت لینک پرداخت. دوباره تلاش کنید.");
                setIsPaying(false);
            }
        } catch (err: any) {
            setError(
                err?.response?.data?.message || "خطایی رخ داد. لطفاً دوباره تلاش کنید."
            );
            setIsPaying(false);
        }
    };

    const isActive = (value: number) => amount === value;

    const payLabel = amount && Number(amount) > 0
        ? `پرداخت ${Number(amount).toLocaleString("fa-IR")} تومان`
        : "پرداخت آنلاین";

    return (
        <div
            dir="rtl"
            className="min-h-screen bg-darkbg text-white flex justify-center items-center px-5 py-8"
        >
            <div className="max-w-[420px] w-full flex flex-col animate-fadeIn">

                <h1 className="text-[18px] font-bold text-white mb-1">شارژ کیف پول</h1>
                <p className="text-[12px] text-white/40 mb-6">مبلغ مورد نظر را انتخاب کنید</p>

                <div className="flex items-center justify-between bg-[#1a1a1a] border border-white/[0.08] rounded-2xl px-5 py-4 mb-6">
                    <div>
                        <p className="text-[11px] text-white/40 mb-1">موجودی کنونی</p>
                        <p className="text-[22px] font-bold text-primary leading-none">
                            {Number(walletBalance).toLocaleString("fa-IR")}
                            <span className="text-[12px] font-normal text-white/40 mr-1">تومان</span>
                        </p>
                    </div>
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <rect x="2" y="5" width="20" height="14" rx="3" stroke="#f4b400" strokeWidth="1.5" />
                            <path d="M16 12a1 1 0 1 0 2 0 1 1 0 0 0-2 0z" fill="#f4b400" />
                            <path d="M2 9h20" stroke="#f4b400" strokeWidth="1.5" />
                        </svg>
                    </div>
                </div>

                <p className="text-[11px] text-white/40 mb-2 tracking-wide">مبالغ پرکاربرد</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                    {QUICK_AMOUNTS.map(({ value, label }) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => selectQuick(value)}
                            className={`py-3 rounded-xl border text-center transition-all active:scale-95
                                ${isActive(value)
                                    ? "bg-primary/10 border-primary"
                                    : "bg-[#1a1a1a] border-white/[0.08] hover:border-white/20"
                                }`}
                        >
                            <p className={`text-[15px] font-bold ${isActive(value) ? "text-primary" : "text-white/80"}`}>
                                {label}
                            </p>
                            <p className="text-[11px] text-white/30 mt-0.5">تومان</p>
                        </button>
                    ))}
                </div>

                <div
                    className={`flex items-center gap-3 bg-[#1a1a1a] border rounded-xl px-4 py-3 mb-4 transition-colors
                        ${error ? "border-red-500/50" : "border-white/[0.08] focus-within:border-primary"}`}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
                        <path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <input
                        type="number"
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder="مبلغ دلخواه"
                        className="flex-1 bg-transparent outline-none text-[17px] font-bold text-white placeholder-white/20 text-left direction-ltr"
                        style={{ direction: "ltr" }}
                    />
                    <span className="text-[11px] text-white/30 shrink-0">تومان</span>
                </div>

                {error && (
                    <p className="text-red-400 text-[12px] px-1 mb-3 -mt-2">{error}</p>
                )}

                <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/[0.06] rounded-xl px-4 py-3 mb-5">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#f4b400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="text-[12px] text-white/40">
                        پرداخت امن از طریق درگاه{" "}
                        <span className="text-primary/80">زرین‌پال</span>
                    </p>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="mr-auto opacity-30">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </div>

                <button
                    type="button"
                    onClick={handlePay}
                    disabled={isPaying}
                    className={`w-full py-4 rounded-xl font-bold text-[15px] text-black mb-2.5 flex items-center justify-center gap-2 transition-all
                        ${isPaying
                            ? "bg-primary/40 cursor-not-allowed"
                            : "bg-primary active:scale-[0.98] hover:brightness-110"
                        }`}
                >
                    {isPaying ? (
                        <>
                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                            در حال انتقال به درگاه...
                        </>
                    ) : (
                        <>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M2 9h20" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M6 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            {payLabel}
                        </>
                    )}
                </button>

                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="w-full py-3.5 bg-[#1a1a1a] border border-white/[0.08] text-white/60 text-[14px] rounded-xl hover:bg-white/5 active:scale-[0.98] transition-all"
                >
                    انصراف
                </button>

                <p className="text-center text-[11px] text-white/25 mt-5 leading-relaxed">
                    با کلیک روی پرداخت، به درگاه امن زرین‌پال منتقل می‌شوید.
                </p>
            </div>
        </div>
    );
}